import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {GoBackToSearchButton} from 'components/Accommodation/BackToSearchButton'
import FacilitiesPopup from 'components/Accommodation/Facilities/FacilitiesPopup'
import {GuestReviewsOverlayPage} from 'components/Accommodation/GuestReviewsOverlay'
import {HotelDescriptions} from 'components/Accommodation/HotelDescriptions/HotelDescriptions'
import {HotelLocationMap} from 'components/Accommodation/HotelLocationMap'
import {LocationOverlayDesktop} from 'components/Accommodation/LocationOverlay'
import {AccommodationOfferBox} from 'components/Accommodation/Offers/AccommodationOfferBox/AccommodationOfferBox'
import {PrimaryRoomOffer} from 'components/Accommodation/Offers/PrimaryRoomOffer'
import {useCachedSearchTopOffers} from 'components/Accommodation/Offers/useCachedSearchTopOffers'
import {PoliciesDetailsOverlay} from 'components/Accommodation/Policies/PoliciesDetailsOverlay'
import {ReloadAP} from 'components/Accommodation/ReloadAPOverlay/ReloadAP'
import {GuestReviewLayout} from 'components/Accommodation/ReviewsSection/constants'
import {RoomsSection} from 'components/Accommodation/Rooms/RoomsSection/RoomsSection'
import {RoomsDetailOverlayContainer} from 'components/Accommodation/RoomsDetailOverlayContainer'
import {AccommodationViewSuggestedHotels} from 'components/Accommodation/SuggestedHotels'
import {ExitPopupAccommodationPage} from 'components/ExitPopup'
import {Footer} from 'components/Footer'
import BackToTopButton from 'components/Pagination/BackToTopButton'
import {PriceWatchCTAWrapper} from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper'
import {ScrollSection, useScrollSpy} from 'components/ScrollSpy'
import {SplitBookingDetails} from 'components/SplitBookings/SplitBookingDetails'
import {OfferTrackingContextProvider} from 'context/offerTracking'
import {OfferViewContext, OfferViewType} from 'context/offerViewContext'
import {useAccommodation} from 'hooks/useAccommodation'
import {useAccommodationPageScrollTopOffset} from 'hooks/useAccommodationPageScrollTopOffset'
import {useDeferredScrollToApTarget} from 'hooks/useDeferredScrollToApTarget'
import {trackAccommodationPageReady} from 'middleware/analytics/actions/trackEvent'
import {
  getIsLightboxOverlayVisible,
  getLightboxSelectedImage
} from 'modules/accommodation/selectors'
import {
  setLightboxOverlayVisibility,
  setLightboxSelectedImage
} from 'modules/accommodation/slice'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {getClickedOfferId} from 'modules/accommodation/utils'
import {getSplitBookingDetails} from 'modules/sapiSearch/selectors'
import {getSearchDesktopHeaderHeight} from 'modules/search/selectors'
import {
  getIsFilteredRoomsResultEmpty,
  getIsRoomsQueryLoading,
  getIsRoomsResultEmpty
} from 'modules/searchApi/selectors'
import {toggle} from 'opticks'
import {imageProvider} from 'utils/imageProvider'

import {useDeviceLayout} from '@daedalus/atlas/context/deviceLayout'
import {Divider} from '@daedalus/atlas/Divider'
import {Box} from '@daedalus/atlas/helpers/Box'
import OpacityAnimation from '@daedalus/atlas/helpers/OpacityAnimation'
import {PageWidthContainer} from '@daedalus/atlas/helpers/PageWidthContainer'
import {StarRating} from '@daedalus/atlas/StarRating'
import {Text} from '@daedalus/atlas/Text'
import {useInView} from '@daedalus/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@daedalus/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity
} from '@daedalus/core/src/analytics/types/Events'
import {LightboxGalleryDesktop} from '@daedalus/shared/src/accommodation/LightboxGalleryDesktop'
import {PriceWatchToggle} from '@daedalus/shared/src/priceWatch/components/PriceWatchToggle/PriceWatchToggle'
import {ContentHotel} from '@findhotel/sapi'

import {AccommodationFixedHeader} from '../../AccommodationFixedHeader/AccommodationFixedHeader'
import {SelectedElementType} from '../../DateGuestInput/ReviewsTrackClick'
import {GridGallery} from '../../GridGallery'
import {HotelDetails} from '../../HotelDetails'
import {LocationSummary} from '../../LocationSummary'
import {PoliciesSection} from '../../PoliciesSection'
import {PopularFacilities} from '../../PopularFacilities'
import {ReviewsSection} from '../../ReviewsSection'
import {getReviewAvailability} from '../../ReviewsSection/utils'
import {ReviewSummary} from '../../ReviewSummary'
import {HotelAccommodationDesktopViewLoading} from './HotelAccommodationDesktopViewLoading'
import {
  DividerContainer,
  DividerContainer700,
  FooterWrapper,
  GridGalleryWrapper,
  HotelDetailsWrapper,
  LeftContentWrapper,
  MainContentWrapper,
  PriceWatchWrapper,
  ReviewSummaryWrapper,
  RightContentWrapper,
  RoomsSectionWrapper,
  Wrapper
} from './styles'

const handleScrollTop = () => {
  window.scrollTo({top: 0, behavior: 'smooth'})
}

interface Props {
  hotel: ContentHotel
  isLoading: boolean
}

export const HotelAccommodationDesktopView: React.FC<Props> = ({
  hotel,
  isLoading
}) => {
  const {objectID: hotelId} = hotel || {}
  const {isDesktopMd, isDesktopLg} = useDeviceLayout()
  const isLargeDesktop = isDesktopLg || isDesktopMd
  const hasRspV2Improvements = toggle(
    '8d14a737-rsp-v2-improvements',
    false,
    true
  )

  const searchDesktopHeaderHeight = useSelector(getSearchDesktopHeaderHeight)

  const galleryViewRootMargin =
    hasRspV2Improvements && isLargeDesktop
      ? `-${searchDesktopHeaderHeight}px 0px 0px 0px`
      : '0px'

  const scrollOffset = useAccommodationPageScrollTopOffset()

  const [hasSimilarHotels, setHasSimilarHotels] = useState(true)
  const dispatch = useDispatch()
  const reviewsRef = useRef<HTMLDivElement>()
  const rightContentRef = useRef<HTMLDivElement>()
  const dispatchTeamEvent = useDispatchTeamEvent()
  useDeferredScrollToApTarget()
  const {onNavigate} = useScrollSpy()
  const {ref: galleryRef, inView: isGalleryInView} = useInView({
    threshold: 0,
    initialInView: true,
    rootMargin: galleryViewRootMargin
  })

  const {ref: roomsSectionRef, entry: roomsIntersectionObserver} = useInView({
    threshold: 0
  })

  const hasScrollPassedRoomSection =
    roomsIntersectionObserver?.boundingClientRect?.bottom < 0

  const {ref: primaryOfferRef, inView: isPrimaryOfferInView} = useInView({
    threshold: 0.4
  })
  const {hasPopularFacilitiesSection, hasFacilitiesSection, hasPolicies} =
    useAccommodation(hotel)

  const isRoomsLoading = useSelector(getIsRoomsQueryLoading)
  const {offerEntity, isComplete, searchedParameters} =
    useCachedSearchTopOffers({hotelId})

  const hasNoFilteredRooms = useSelector(getIsFilteredRoomsResultEmpty)
  const hasNoRooms = useSelector(getIsRoomsResultEmpty)

  const selectedVioOfferId = getClickedOfferId()

  const {hasReviews} = getReviewAvailability(hotel)

  const {hasSplitBookingOffer} = useSelector(state =>
    getSplitBookingDetails(state, hotelId)
  )

  const isLightboxOverlayVisible = useSelector(getIsLightboxOverlayVisible)

  const handleLocationSummaryClick = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsLocationSummary,
        action: Action.Clicked
      })
    )
    // Scroll down to hotel location
    onNavigate(AccommodationPageNavSections.HotelLocation)
  }
  const handleReviewSummaryClick = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsReviewSummary,
        action: Action.Clicked,
        payload: {
          selectedElement: SelectedElementType.Rating
        }
      })
    )

    if (hasReviews) {
      // Scroll to review section
      onNavigate(AccommodationPageNavSections.Reviews)
    }
  }

  const lightboxSelectedImage = useSelector(getLightboxSelectedImage)

  const onLightboxGalleryClose = useCallback(() => {
    dispatch(setLightboxOverlayVisibility(false))
    dispatch(setLightboxSelectedImage(undefined))
  }, [dispatch])

  const onLightboxGalleryVisible = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.FullScreenImageGallery,
        action: Action.Displayed
      })
    )
  }

  const trackLightboxGalleryThumbnail = useCallback(() => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.VerticalImageGallery,
        action: Action.Scrolled
      })
    )
  }, [dispatchTeamEvent])

  const onLightboxGalleryCarouselSwipe = useCallback(() => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.FullScreenImageGallery,
        action: Action.Scrolled
      })
    )
  }, [dispatchTeamEvent])

  useEffect(() => {
    dispatch(trackAccommodationPageReady())
  }, [dispatch])

  // Show page loading
  if (isLoading || !hotel) return <HotelAccommodationDesktopViewLoading />

  const isOffersOrRoomsLoading = !isComplete || isRoomsLoading
  return (
    <OfferViewContext.Provider value={OfferViewType.AP_DESKTOP}>
      <AccommodationFixedHeader
        isVisible={!isGalleryInView && !process.env.STORYBOOK}
        isCtaVisible={
          !hasNoRooms &&
          toggle(
            '8d14a737-rsp-v2-improvements',
            !isPrimaryOfferInView,
            hasScrollPassedRoomSection
          )
        }
        hasReviews={hasReviews}
        hasFacilities={hasFacilitiesSection}
        hasPolicies={hasPolicies}
        isLoading={isOffersOrRoomsLoading}
      />
      <Wrapper>
        <PageWidthContainer paddingDesktopXsSize="s500">
          <ScrollSection name={AccommodationPageNavSections.Photos}>
            <GoBackToSearchButton />
            <GridGalleryWrapper ref={galleryRef} id="photos">
              <GridGallery images={hotel.imageURIs || []} />
            </GridGalleryWrapper>
          </ScrollSection>
          <MainContentWrapper>
            <LeftContentWrapper>
              <HotelDetailsWrapper>
                <HotelDetails hotel={hotel} />
              </HotelDetailsWrapper>
              <LocationSummary
                hotel={hotel}
                onLocationSummaryClick={handleLocationSummaryClick}
              />
              <ReviewSummaryWrapper>
                <ReviewSummary
                  hotel={hotel}
                  onReviewSummaryClick={handleReviewSummaryClick}
                />
              </ReviewSummaryWrapper>

              <DividerContainer700 />
              {hasPopularFacilitiesSection && (
                <>
                  <ScrollSection
                    name={AccommodationPageNavSections.Amenities}
                    scrollTopMargin={scrollOffset}
                  >
                    <PopularFacilities hotel={hotel} />
                  </ScrollSection>
                </>
              )}
              {toggle(
                'sapi4eva-hotel-descriptions',
                null,
                <>
                  <Box paddingTop="s600" paddingBottom="s600">
                    <Divider />
                  </Box>
                  <HotelDescriptions hotel={hotel} />
                </>
              )}
            </LeftContentWrapper>
            <RightContentWrapper
              ref={rightContentRef}
              scrollTopMargin={scrollOffset}
            >
              <PriceWatchWrapper>
                <PriceWatchCTAWrapper
                  hotel={hotel}
                  ctaComponent={PriceWatchToggle}
                  componentName="PriceWatchToggle"
                  holdOutElement={null}
                  searchIsCompleted={!isOffersOrRoomsLoading}
                />
              </PriceWatchWrapper>
              <OfferTrackingContextProvider
                offerEntity={offerEntity}
                parameters={searchedParameters}
              >
                <AccommodationOfferBox
                  offerEntity={offerEntity}
                  isLoading={isOffersOrRoomsLoading}
                  primaryOfferRef={primaryOfferRef}
                  selectedVioOfferId={selectedVioOfferId}
                />
              </OfferTrackingContextProvider>
            </RightContentWrapper>
          </MainContentWrapper>
        </PageWidthContainer>
        <Box paddingTop="s900">
          <Divider />
        </Box>
        <RoomsSectionWrapper ref={roomsSectionRef}>
          <PageWidthContainer paddingDesktopXsSize="s500" maxPageWidth={948}>
            <RoomsSection hotelId={hotelId} />
          </PageWidthContainer>
        </RoomsSectionWrapper>
        <Box paddingBottom="s900">
          <Divider />
        </Box>
        <PageWidthContainer paddingDesktopXsSize="s500">
          {hasReviews && (
            <>
              <ScrollSection
                name={AccommodationPageNavSections.Reviews}
                scrollTopMargin={scrollOffset}
              >
                <ReviewsSection
                  anchorRef={reviewsRef}
                  hotel={hotel}
                  layoutConfig={GuestReviewLayout.FULL}
                />
              </ScrollSection>
              <DividerContainer />
            </>
          )}
          <ScrollSection
            name={AccommodationPageNavSections.HotelLocation}
            scrollTopMargin={scrollOffset}
          >
            <HotelLocationMap hotel={hotel} />
          </ScrollSection>

          {hasPolicies && (
            <>
              <DividerContainer />
              <ScrollSection
                name={AccommodationPageNavSections.Policies}
                scrollTopMargin={scrollOffset}
              >
                <PoliciesSection hotel={hotel} />
              </ScrollSection>
            </>
          )}
        </PageWidthContainer>
        {hasSimilarHotels && !hasNoFilteredRooms && (
          <AccommodationViewSuggestedHotels
            onHotelsLoadingComplete={setHasSimilarHotels}
            hotelId={hotelId}
            hasPadding
          />
        )}
        <BackToTopButton enabled onClick={handleScrollTop} />
      </Wrapper>
      <OpacityAnimation delay={1500}>
        <FooterWrapper>
          <Footer forcedLayout="wide" />
        </FooterWrapper>
      </OpacityAnimation>
      <LightboxGalleryDesktop
        images={hotel.imageURIs || []}
        isVisible={isLightboxOverlayVisible}
        selectedImage={lightboxSelectedImage}
        onClose={onLightboxGalleryClose}
        onVisible={onLightboxGalleryVisible}
        imageProvider={imageProvider}
        trackLightboxGalleryThumbnail={trackLightboxGalleryThumbnail}
        onLightboxGalleryCarouselSwipe={onLightboxGalleryCarouselSwipe}
        hotelDetailsContent={
          <Box display="flex" alignItems="center">
            <Text variant="titleM">{hotel.hotelName}</Text>
            <Box paddingRight="s200" paddingLeft="s200" paddingTop="s200">
              <StarRating
                size="xs"
                data-id="StarRating"
                rating={hotel.starRating}
                isInline
              />
            </Box>
          </Box>
        }
        offerContent={
          !hasNoFilteredRooms ? (
            <div data-id="StaticOffer">
              <PrimaryRoomOffer hotelId={hotelId} component="DesktopGallery" />
            </div>
          ) : null
        }
      />
      <LocationOverlayDesktop
        hotel={hotel}
        isLoading={isOffersOrRoomsLoading}
        hasOffer={!hasNoFilteredRooms}
      />
      {hasSplitBookingOffer && <SplitBookingDetails isAccommodationPage />}
      <RoomsDetailOverlayContainer />
      <PoliciesDetailsOverlay hotelId={hotelId} />
      <FacilitiesPopup hotel={hotel} />

      <GuestReviewsOverlayPage hotel={hotel} />

      {/* added as part of 16b5e700-exit-popup-desktop-ap */}
      <ExitPopupAccommodationPage
        hotel={hotel}
        offerEntity={offerEntity}
        isLoading={!hotel || !isComplete}
      />
      <ReloadAP />
    </OfferViewContext.Provider>
  )
}
